import { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { categoryAPI } from "../../lib/api";
import UserLayout from "../../components/UserLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const AdminCategories = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        description: "",
        is_active: true
    });

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            const response = await categoryAPI.getAll();
            setCategories(response.data);
        } catch (error) {
            toast.error("Gagal memuat data kategori");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            if (editing) {
                await categoryAPI.update(editing, formData);
                toast.success("Kategori berhasil diupdate");
            } else {
                await categoryAPI.create(formData);
                toast.success("Kategori berhasil dibuat");
            }
            setDialogOpen(false);
            fetchCategories();
            resetForm();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Gagal menyimpan kategori");
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (cat) => {
        setEditing(cat.id);
        setFormData({
            name: cat.name,
            description: cat.description || "",
            is_active: cat.is_active
        });
        setDialogOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Yakin ingin menghapus kategori ini?")) return;
        
        try {
            await categoryAPI.delete(id);
            toast.success("Kategori berhasil dihapus");
            fetchCategories();
        } catch (error) {
            toast.error("Gagal menghapus kategori");
        }
    };

    const resetForm = () => {
        setEditing(null);
        setFormData({ name: "", description: "", is_active: true });
    };

    return (
        <UserLayout>
            <div className="space-y-6" data-testid="admin-categories">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="font-heading text-2xl font-bold text-gray-900">Kelola Kategori</h1>
                        <p className="text-gray-600">Atur kategori produk</p>
                    </div>
                    <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="rounded-xl" data-testid="add-category-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Tambah Kategori
                    </Button>
                </div>

                <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Nama</TableHead>
                                <TableHead>Deskripsi</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="text-right">Aksi</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={4} className="text-center py-8">
                                        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                                    </TableCell>
                                </TableRow>
                            ) : categories.length > 0 ? (
                                categories.map((cat) => (
                                    <TableRow key={cat.id}>
                                        <TableCell className="font-medium">{cat.name}</TableCell>
                                        <TableCell className="text-gray-500">{cat.description || "-"}</TableCell>
                                        <TableCell>
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${cat.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                                {cat.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-2">
                                                <Button size="icon" variant="ghost" onClick={() => handleEdit(cat)} data-testid={`edit-cat-${cat.id}`}>
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                <Button size="icon" variant="ghost" className="text-red-500" onClick={() => handleDelete(cat.id)} data-testid={`delete-cat-${cat.id}`}>
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={4} className="text-center py-8 text-gray-500">
                                        Belum ada kategori
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>

                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>{editing ? "Edit Kategori" : "Tambah Kategori Baru"}</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label>Nama Kategori</Label>
                                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="category-name-input" />
                            </div>
                            <div className="space-y-2">
                                <Label>Deskripsi</Label>
                                <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} data-testid="category-desc-input" />
                            </div>
                            <div className="flex items-center justify-between">
                                <Label>Status Active</Label>
                                <Switch checked={formData.is_active} onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })} />
                            </div>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Batal</Button>
                                <Button type="submit" disabled={saving} data-testid="save-category-btn">
                                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                    {saving ? "Menyimpan..." : "Simpan"}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
        </UserLayout>
    );
};

export default AdminCategories;
